import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { flashcardApi, marketplaceApi, reviewApi } from '../utils/api';
import ChessDiagram from '../components/ChessDiagram';

const STAR_DESCRIPTIONS = {
  1: 'Minimally (the deck was of little or no value to me)',
  2: 'Somewhat (the deck was of some value to me)',
  3: 'Average (the deck was ok)',
  4: 'Very (the deck was very useful to me)',
  5: 'Extraordinary (the deck was tremendously helpful)',
};

const MAX_REVIEWS = 5;

function ReviewModal({ deck, onClose, token }) {
  const [rating, setRating]           = useState(0);
  const [hovered, setHovered]         = useState(0);
  const [reviewText, setReviewText]   = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');
  const [existingCount, setExistingCount] = useState(null); // null = loading
  const [loadError, setLoadError]     = useState('');

  // On open, fetch how many reviews this user already has for this deck
  useEffect(() => {
    let cancelled = false;
    reviewApi.getMyReviews(deck.mpo_id, token)
      .then(data => { if (!cancelled) setExistingCount((data || []).length); })
      .catch(err => { if (!cancelled) setLoadError(err.message); });
    return () => { cancelled = true; };
  }, [deck.mpo_id, token]);

  const atLimit = existingCount !== null && existingCount >= MAX_REVIEWS;

  const handleSubmit = async () => {
    if (rating < 1) { setError('Please select a star rating.'); return; }
    setSubmitting(true);
    try {
      await reviewApi.create(deck.mpo_id, rating, reviewText, token);
      onClose(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const display = hovered || rating;

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">How useful did you find this Flashcard deck to be?</h5>
            <button type="button" className="btn-close" onClick={() => onClose(false)} />
          </div>
          <div className="modal-body">
            <p className="text-muted small mb-3">{deck.title}</p>

            {/* Loading state */}
            {existingCount === null && !loadError && (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-secondary" />
              </div>
            )}

            {loadError && (
              <div className="alert alert-danger py-2 small">{loadError}</div>
            )}

            {/* At limit */}
            {atLimit && (
              <div className="alert alert-warning py-2 mb-0" style={{ fontSize: '0.88rem' }}>
                You have already posted {MAX_REVIEWS} reviews for this deck — the maximum allowed.
                No further reviews can be submitted.
              </div>
            )}

            {/* Review form — only when under the limit */}
            {existingCount !== null && !atLimit && (
              <>
                {existingCount > 0 && (
                  <p className="text-muted small mb-3">
                    You have posted {existingCount} of {MAX_REVIEWS} allowed reviews for this deck.
                  </p>
                )}
                {/* Star selector */}
                <div className="d-flex gap-1 mb-2" style={{ fontSize: '2rem', cursor: 'pointer' }}>
                  {[1,2,3,4,5].map(s => (
                    <span
                      key={s}
                      style={{ color: s <= display ? '#f5a623' : '#ccc', transition: 'color 0.1s' }}
                      onMouseEnter={() => setHovered(s)}
                      onMouseLeave={() => setHovered(0)}
                      onClick={() => setRating(s)}
                    >★</span>
                  ))}
                </div>
                {display > 0 && (
                  <p className="text-muted small mb-3">{display} star{display > 1 ? 's' : ''} — {STAR_DESCRIPTIONS[display]}</p>
                )}
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Optional comments…"
                  value={reviewText}
                  onChange={e => setReviewText(e.target.value)}
                />
                {error && <div className="alert alert-danger mt-2 mb-0 py-1 small">{error}</div>}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={() => onClose(false)}>
              {atLimit ? 'Close' : 'Cancel'}
            </button>
            {!atLimit && existingCount !== null && (
              <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Review'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { token, prefs } = useAuth();
  const navigate  = useNavigate();
  const [cards, setCards]               = useState([]);
  const [purchases, setPurchases]       = useState([]);   // full offering objects for purchased decks
  const [filter, setFilter]             = useState('');
  const [showMyCards, setShowMyCards]   = useState(true);
  const [showPurchases, setShowPurchases] = useState(true);
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(true);
  const [reviewDeck, setReviewDeck]     = useState(null); // deck being reviewed

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cardData, allOfferings, myPurchases] = await Promise.all([
        flashcardApi.list(token),
        marketplaceApi.list(token),
        marketplaceApi.myPurchases(token),
      ]);
      setCards(cardData || []);
      const purchasedIds = new Set((myPurchases || []).map(p => p.mpo_id));
      setPurchases((allOfferings || []).filter(o => purchasedIds.has(o.mpo_id)));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this flashcard?')) return;
    try {
      await flashcardApi.delete(id, token);
      setCards(cs => cs.filter(c => c.id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const q = filter.toLowerCase();
  const visibleCards = showMyCards
    ? cards.filter(c =>
        c.name.toLowerCase().includes(q) ||
        (c.problem || '').toLowerCase().includes(q)
      )
    : [];

  const visiblePurchases = showPurchases
    ? purchases.filter(p =>
        p.title.toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q)
      )
    : [];

  const hasContent = cards.length > 0 || purchases.length > 0;

  if (loading) return <div className="text-center mt-5"><div className="spinner-border" /></div>;

  return (
    <>
      {reviewDeck && (
        <ReviewModal
          deck={reviewDeck}
          token={token}
          onClose={(submitted) => {
            setReviewDeck(null);
            if (submitted) alert('Thank you for your review!');
          }}
        />
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">My Flashcards</h2>
        <Link to="/flashcards/new" className="btn btn-success">+ New Card</Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Filter row */}
      {hasContent && (
        <div className="d-flex align-items-center gap-3 mb-3">
          <input
            className="form-control"
            style={{ width: '50%' }}
            placeholder="Filter by name or problem…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          <div className="form-check mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="chk-mycards"
              checked={showMyCards}
              onChange={e => setShowMyCards(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="chk-mycards">My Cards</label>
          </div>
          <div className="form-check mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="chk-purchases"
              checked={showPurchases}
              onChange={e => setShowPurchases(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="chk-purchases">Purchases</label>
          </div>
        </div>
      )}

      {/* Empty state */}
      {visibleCards.length === 0 && visiblePurchases.length === 0 && !loading && (
        <div className="text-muted">
          {!hasContent
            ? 'No flashcards yet. Click "+ New Card" to create your first one.'
            : 'No cards match your filter.'}
        </div>
      )}

      {/* ── My Cards ────────────────────────────────────────────────── */}
      {visibleCards.length > 0 && (
        <div className="row row-cols-1 row-cols-md-2 g-3 mb-4">
          {visibleCards.map(card => (
            <div className="col" key={card.id}>
              <div className="card h-100 card-flashcard shadow-sm">
                <div className="card-body d-flex gap-3" style={{ minHeight: 220 }}>
                  {card.fen && (
                    <div className="flex-shrink-0" style={{ lineHeight: 0, alignSelf: 'flex-start' }}>
                      <ChessDiagram fen={card.fen}
                        prefs={{ ...prefs, diagramFlipped: card.flipped ?? prefs.diagramFlipped, diagramShowSideToMoveDisc: card.show_side_to_move ?? prefs.diagramShowSideToMoveDisc }}
                        size={184} />
                    </div>
                  )}
                  <div className="flex-grow-1 min-width-0">
                    <h5 className="card-title mb-1">{card.name}</h5>
                    {card.problem && (
                      <p className="card-text text-muted small mb-0"
                         style={{ whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 5,
                                  WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {card.problem}
                      </p>
                    )}
                  </div>
                </div>
                <div className="card-footer bg-transparent d-flex gap-2">
                  <button className="btn btn-sm btn-outline-primary"
                    onClick={() => navigate(`/flashcards/${card.id}`)}>View</button>
                  <button className="btn btn-sm btn-outline-secondary"
                    onClick={() => navigate(`/flashcards/${card.id}/edit`)}>Edit</button>
                  <button className="btn btn-sm btn-outline-danger ms-auto"
                    onClick={() => handleDelete(card.id)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Purchased Decks ──────────────────────────────────────────── */}
      {visiblePurchases.length > 0 && (
        <>
          {showMyCards && visibleCards.length > 0 && <hr className="my-3" />}
          <h5 className="mb-3 text-muted" style={{ fontSize: '0.95rem', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
            Purchased Decks
          </h5>
          <div className="row row-cols-1 row-cols-md-2 g-3">
            {visiblePurchases.map(deck => (
              <div className="col" key={deck.mpo_id}>
                <div className="card h-100 shadow-sm border-0" style={{ borderRadius: '0.75rem', overflow: 'hidden' }}>
                  <div style={{ background: 'linear-gradient(135deg, var(--theme-brand-nav) 0%, #2d5f8a 100%)', padding: '0.9rem 1.1rem' }}>
                    <h5 className="mb-0 text-white fw-semibold" style={{ fontSize: '0.95rem' }}>{deck.title}</h5>
                    <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', marginTop: '0.25rem' }}>
                      {(deck.flashcards || []).length} cards
                    </div>
                  </div>
                  {deck.description && (
                    <div className="px-3 py-2" style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)', borderBottom: '1px solid var(--bs-border-color)' }}>
                      <p className="mb-0" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {deck.description}
                      </p>
                    </div>
                  )}
                  <div className="card-footer bg-transparent d-flex gap-2">
                    <button className="btn btn-sm btn-outline-primary"
                      onClick={() => navigate(`/purchased-deck/${deck.mpo_id}`)}>View</button>
                    <button className="btn btn-sm btn-outline-secondary"
                      onClick={() => navigate(`/purchased-deck/${deck.mpo_id}?mode=quiz`)}>Quiz</button>
                    <button className="btn btn-sm btn-outline-warning ms-auto"
                      onClick={() => setReviewDeck(deck)}>Post review…</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
